import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Number of tabs, taken in order, that fit within `available` on one row.
function countThatFits(widths: number[], available: number, gap: number) {
  let used = 0;
  let count = 0;
  for (const width of widths) {
    const next = count === 0 ? width : used + gap + width;
    if (next > available) break;
    used = next;
    count += 1;
  }
  return count;
}

export function useTabRowFit<T extends { id: string }>(
  tabs: T[],
  activeTabId: string | null
) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);

  const measure = useCallback(() => {
    const row = rowRef.current;
    const measureEl = measureRef.current;
    if (!row || !measureEl) return;

    const containerWidth = row.clientWidth;
    if (containerWidth === 0) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;

    const children = Array.from(measureEl.children) as HTMLElement[];
    if (children.length === 0) return;
    const widths = children.slice(0, -1).map((el) => el.offsetWidth);
    const moreWidth = children[children.length - 1].offsetWidth;

    const fitting = countThatFits(widths, containerWidth, gap);
    if (fitting === widths.length) {
      setVisibleCount(fitting);
      return;
    }
    // Some tabs overflow, so the row also has to hold the "+N More" pill.
    setVisibleCount(
      countThatFits(widths, containerWidth - moreWidth - gap, gap)
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    const row = rowRef.current;
    const measureEl = measureRef.current;
    if (!row || !measureEl) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(measureEl);
    return () => observer.disconnect();
  }, [measure, tabs.length]);

  const visible = tabs.slice(0, visibleCount);
  const overflow = tabs.slice(visibleCount);
  // Keep the active tab visible when it falls into the overflow set.
  const activeOverflowIdx = activeTabId
    ? overflow.findIndex((tab) => tab.id === activeTabId)
    : -1;
  if (activeOverflowIdx < 0 || visibleCount === 0) {
    return { rowRef, measureRef, visibleTabs: visible, overflowTabs: overflow };
  }
  const demoted = visible[visibleCount - 1];
  return {
    rowRef,
    measureRef,
    visibleTabs: [...visible.slice(0, -1), overflow[activeOverflowIdx]],
    overflowTabs: overflow.map((tab, i) =>
      i === activeOverflowIdx ? demoted : tab
    ),
  };
}

export default useTabRowFit;
